import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';

import { getConfig } from '@zimpel/common/config';
import LoadingIndicator from '@zimpel/common/components/LoadingIndicator';
import t from '@zimpel/common/helpers/i18n';
import { PrimaryButton } from '@zimpel/common/components/Buttons';
import { H1 } from '@zimpel/common/components/Headlines';
import { BigBlock } from '@zimpel/common/components/Blocks';
import { ContentBody, ContentNarrow } from '@zimpel/common/components/ContentLayout';
import { UrlParamMissingError } from '@zimpel/common/errors';
import { useNotificationContext } from '@zimpel/AppContext';
import * as api from './api';

const VerifyEmailPage: React.FC = () => {
  const { verificationResult, tenantId } = useVerification();
  const { notifySuccess } = useNotificationContext();

  useEffect(() => {
    if (verificationResult === 'successForCurrentUser') {
      notifySuccess(t('email_verification.success_notification'));
    }
  });

  if (verificationResult === null) {
    return <LoadingIndicator />;
  }

  if (verificationResult === 'successForCurrentUser') {
    return <Navigate to={`/tenants/${tenantId}`} replace />;
  }

  if (verificationResult === 'successForOtherUser') {
    return <EmailVerifiedPlainPage />;
  }

  return <EmailVerifiedPlainPage showLogin />;
};

const EmailVerifiedPlainPage: React.FC<{ showLogin?: boolean }> = ({ showLogin }) => (
  <ContentBody>
    <ContentNarrow>
      <H1>{t('email_verified_page.header')}</H1>

      <BigBlock>
        {t('email_verified_page.text')}
      </BigBlock>

      {showLogin && (
        <BigBlock>
          <PrimaryButton $width="l" as="a" href="/login">
            {t('email_verified_page.login')}
          </PrimaryButton>
        </BigBlock>
      )}
    </ContentNarrow>
  </ContentBody>
);

type VerificationState = 'successForCurrentUser' | 'successForOtherUser' | 'successNotLoggedIn' | null;

const useVerification = () => {
  const { accountId } = getConfig();
  const { verificationCode } = useParams<{ verificationCode: string }>();
  if (!verificationCode) {
    throw new UrlParamMissingError('verificationCode');
  }
  const [verificationResult, setVerificationResult] = useState<VerificationState | 'successNotLoggedIn' | null>(null);
  const [tenantId, setTenantId] = useState<number | null>(null);

  const verify = async () => {
    const result = await api.verifyEmail({ verificationCode });
    const { tenantId } = result;

    if (tenantId) {
      setTenantId(tenantId);
      setVerificationResult('successForCurrentUser');
    } else if (accountId) {
      setVerificationResult('successForOtherUser');
    } else {
      setVerificationResult('successNotLoggedIn');
    }
  };

  useEffect(() => { verify(); }, [verificationCode]);

  return { verificationResult, tenantId };
};

export default VerifyEmailPage;
